import React from 'react';
import { Icon } from './components.jsx';
// WSM Policies - read-only browser for NetDocuments Worksite Security
// Manager policies + their ACL membership. Fills the gap in ND's own admin
// UI, which doesn't show who's in each policy. IT-only (the whole app is
// IT-gated); the /api/nd/wsm/* endpoints are requireIT + read-only.

const { useState, useEffect, useCallback, useMemo, useRef } = React;

const wApi  = (path, tag) => window.IT_AUTH.api('GET', path, undefined, tag || 'wsm');
const wPost = (path, body, tag) => window.IT_AUTH.api('POST', path, body, tag || 'wsm');

// Race any ND-backed call against a timeout so a hung/unresponsive ND
// connection surfaces a clear message instead of spinning forever.
const withTimeout = (p, ms, msg) => Promise.race([
  p,
  new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms)),
]);

// Parse client + matter numbers out of a policy name like "EW (05311.001)".
const parseClientMatter = (name) => {
  const m = /\((\d+)\.(\d+)\)/.exec(name || '');
  return m ? { client: m[1], matter: m[2] } : null;
};

export function WsmPoliciesView() {
  const [policies, setPolicies]   = useState(null);  // null = loading
  const [error, setError]         = useState(null);
  const [q, setQ]                 = useState('');
  const [expandedId, setExpanded] = useState(null);
  const [members, setMembers]     = useState({});    // policyId -> {loading|error|data}
  const [audit, setAudit]         = useState(null);  // null | {running} | {result}

  const loadPolicies = useCallback(async () => {
    setPolicies(null); setError(null); setExpanded(null); setMembers({}); setAudit(null);
    try {
      const r = await withTimeout(
        wApi(`/nd/wsm/policies?cabinet=live`, 'wsmList'), 30000,
        'NetDocuments didn’t respond. It may be overloaded or the connection dropped - wait a minute and reload, or reconnect at Settings › NetDocuments.');
      setPolicies(Array.isArray(r?.policies) ? r.policies : []);
    } catch (e) {
      setError(e.message || 'Failed to load policies');
      setPolicies([]);
    }
  }, []);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const filtered = useMemo(() => {
    const list = policies || [];
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter(p =>
      (p.name || '').toLowerCase().includes(term) || (p.id || '').toLowerCase().includes(term));
  }, [policies, q]);

  // Sets of ids flagged by the last audit, for the per-row markers.
  const notAppliedIds = useMemo(
    () => new Set((audit?.result?.notApplied || []).map(x => x.id)), [audit]);
  const missingLockIds = useMemo(
    () => new Set((audit?.result?.missingLock || []).map(x => x.id)), [audit]);

  const cancelRef = useRef(false);

  // Chunked audit so the ~900-policy run shows live progress in a modal
  // instead of a frozen button. Each chunk is one request to the audit
  // endpoint; results accumulate. Cancellable between chunks.
  const runAudit = async () => {
    if (audit?.running) return;
    const items = filtered.map(p => ({ id: p.id, name: p.name }));
    // Live-scanning the whole set is heavy on ND. Nudge toward filtering
    // first; a big run is opt-in.
    if (items.length > 150 && !window.confirm(
        `This checks ${items.length} policies live against NetDocuments and can be slow and heavy on the connection. ` +
        `Searching to narrow the list first is recommended. Run on all ${items.length} anyway?`)) {
      return;
    }
    const total = items.length;
    const CHUNK = 20;
    const acc = {
      checked: 0, appliedCount: 0, notAppliedCount: 0, notApplied: [],
      lockedCount: 0, missingLockCount: 0, missingLock: [], lockCheckedCount: 0, errorCount: 0,
    };
    cancelRef.current = false;
    setAudit({ running: true, total, done: 0, acc: { ...acc } });

    // Post one chunk, retrying with backoff if ND throttles - so a
    // transient rate-limit window doesn't abort the whole run; we just
    // wait for it to reset and continue.
    const postChunk = async (chunk, attempt = 1) => {
      try {
        return await withTimeout(
          wPost('/nd/wsm/compliance-audit', { cabinet: 'live', policies: chunk }, 'wsmAudit'), 90000,
          'NetDocuments timed out on this batch.');
      } catch (e) {
        if (attempt < 4 && !cancelRef.current) {
          await new Promise(r => setTimeout(r, attempt * 8000)); // 8s, 16s, 24s backoff
          return postChunk(chunk, attempt + 1);
        }
        throw e;
      }
    };

    try {
      for (let i = 0; i < items.length; i += CHUNK) {
        if (cancelRef.current) { setAudit({ result: acc, cancelled: true }); return; }
        const chunk = items.slice(i, i + CHUNK);
        const r = await postChunk(chunk);
        acc.checked          += r.checked || 0;
        acc.appliedCount     += r.appliedCount || 0;
        acc.notAppliedCount  += r.notAppliedCount || 0;
        acc.lockedCount      += r.lockedCount || 0;
        acc.missingLockCount += r.missingLockCount || 0;
        acc.lockCheckedCount += r.lockCheckedCount || 0;
        acc.errorCount       += r.errorCount || 0;
        acc.notApplied.push(...(r.notApplied || []));
        acc.missingLock.push(...(r.missingLock || []));
        setAudit({ running: true, total, done: Math.min(i + CHUNK, total),
          acc: { ...acc, notApplied: [...acc.notApplied], missingLock: [...acc.missingLock] } });
      }
      setAudit({ result: acc });
    } catch (e) {
      setAudit({ error: e.message || 'Compliance check failed', result: acc.checked ? acc : undefined });
    }
  };

  const toggle = async (policy) => {
    if (expandedId === policy.id) { setExpanded(null); return; }
    setExpanded(policy.id);
    if (members[policy.id]?.data || members[policy.id]?.loading) return;
    setMembers(m => ({ ...m, [policy.id]: { loading: true } }));
    const cm = parseClientMatter(policy.name);
    const cmQs = cm ? `&client=${cm.client}&matter=${cm.matter}` : '';
    try {
      const r = await wApi(`/nd/wsm/policies/${encodeURIComponent(policy.id)}/members?cabinet=live${cmQs}`, 'wsmMembers');
      setMembers(m => ({ ...m, [policy.id]: { data: r } }));
    } catch (e) {
      setMembers(m => ({ ...m, [policy.id]: { error: e.message || 'Failed to load members' } }));
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.headerRow}>
          <h1 style={S.h1}>WSM Policies</h1>
          {policies && <span style={S.count}>{filtered.length}{q ? ` of ${policies.length}` : ''}</span>}
          <div style={{ flex: 1 }}/>
          <button style={S.auditBtn} onClick={runAudit} disabled={!policies || audit?.running}>
            {audit?.running ? `Checking ${audit.checking}…` : `Run compliance check (${filtered.length})`}
          </button>
        </div>
        <div style={S.searchRow}>
          <Icon name="search" size={14}/>
          <input style={S.search} value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by policy name or id…  e.g. 05311.001"/>
        </div>
      </div>

      {audit?.running && <AuditProgress audit={audit} onCancel={() => { cancelRef.current = true; }}/>}
      {audit?.result && !audit.running && <AuditBanner result={audit.result} cancelled={audit.cancelled}/>}
      {audit?.error && <div style={S.err}>{audit.error}</div>}

      <div style={S.list}>
        {policies === null && <div style={S.muted}>Loading policies from NetDocuments…</div>}
        {error && <div style={S.err}>{error}</div>}
        {policies && !error && filtered.length === 0 && (
          <div style={S.muted}>{q ? 'No policies match your search.' : 'No WSM policies found in this cabinet.'}</div>
        )}
        {filtered.map(p => {
          const isOpen = expandedId === p.id;
          const m = members[p.id];
          const notApplied  = notAppliedIds.has(p.id);
          const missingLock = missingLockIds.has(p.id);
          return (
            <div key={p.id} style={S.row}>
              <div style={S.rowHead} onClick={() => toggle(p)}>
                <Icon name={isOpen ? 'chevron' : 'chevronRight'} size={12}/>
                <span style={S.pName}>{p.name}</span>
                <span style={S.rowFlags}>
                  {notApplied  && <span title="Not applied to any workspace" style={S.rowWarn}>⚠ not applied</span>}
                  {missingLock && <span title="Matter has no lock symbol" style={S.rowWarn}>🔓 no lock</span>}
                </span>
              </div>
              {isOpen && (
                <div style={S.detail}>
                  {m?.loading && <div style={S.mutedSm}>Loading members…</div>}
                  {m?.error && <div style={S.err}>{m.error}</div>}
                  {m?.data && <MembersPanel data={m.data}/>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AuditProgress({ audit, onCancel }) {
  const { total = 0, done = 0, acc = {} } = audit;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div style={S.modalWrap}>
      <div style={S.modal}>
        <div style={S.modalTitle}>Running compliance check</div>
        <div style={S.modalSub}>Deliberately slow to keep NetDocuments happy - roughly a minute per ~100 policies. Leave it running; you can stop any time.</div>
        <div style={S.progTrack}><div style={{ ...S.progFill, width: `${pct}%` }}/></div>
        <div style={S.progText}>{done} of {total} policies · {pct}%</div>
        <div style={S.modalStats}>
          <span>⚠ {acc.notAppliedCount || 0} not applied</span>
          <span>🔓 {acc.missingLockCount || 0} missing lock</span>
        </div>
        <div style={S.modalFoot}>
          <button style={S.modalCancel} onClick={onCancel}>Stop &amp; show what’s found</button>
        </div>
      </div>
    </div>
  );
}

// Group a list of {id, name} exceptions by client number (parsed from the
// "EW (client.matter)" name), so results read as "client → its matters"
// instead of one long flat scroll. Unparseable names go under "Other".
const groupByClient = (items) => {
  const groups = {};
  for (const p of items) {
    const m = /\((\d+)\.(\d+)\)/.exec(p.name || '');
    const key = m ? m[1] : 'Other';
    (groups[key] = groups[key] || []).push({ matter: m ? m[2] : p.name, name: p.name });
  }
  return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
};

// Download the audit exceptions as a CSV (opens directly in Excel). BOM
// prefix so Excel reads UTF-8 (names/emoji) correctly.
const exportAuditCsv = (result) => {
  const rows = [['Issue', 'Client', 'Matter', 'Policy name']];
  const add = (issue, list) => {
    for (const p of (list || [])) {
      const m = /\((\d+)\.(\d+)\)/.exec(p.name || '');
      rows.push([issue, m ? m[1] : '', m ? m[2] : '', p.name || '']);
    }
  };
  add('Not applied to workspace', result.notApplied);
  add('Missing lock', result.missingLock);
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `wsm-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

function AuditBanner({ result, cancelled }) {
  const [show, setShow] = useState(null); // 'notApplied' | 'missingLock' | null
  const {
    checked = 0, appliedCount = 0,
    notAppliedCount = 0, notApplied = [],
    lockCheckedCount = 0, missingLockCount = 0, missingLock = [],
    errorCount = 0,
  } = result;
  const hasExceptions = notAppliedCount > 0 || missingLockCount > 0;
  const list = show === 'notApplied' ? notApplied : show === 'missingLock' ? missingLock : [];
  const groups = list.length ? groupByClient(list) : [];
  return (
    <div style={S.banner}>
      <div style={S.bannerRow}>
        {cancelled && <span style={{ ...S.bannerStat, color: 'var(--text-subtle)' }}>(stopped early)</span>}
        <span style={S.bannerStat}>Checked <b>{checked}</b></span>
        <span style={S.bannerStat}>✓ <b>{appliedCount}</b> applied</span>
        <span style={{ ...S.bannerStat, color: notAppliedCount ? 'oklch(55% 0.15 40)' : undefined, cursor: notAppliedCount ? 'pointer' : undefined }}
          onClick={() => notAppliedCount && setShow(s => s === 'notApplied' ? null : 'notApplied')}>
          ⚠ <b>{notAppliedCount}</b> not applied{notAppliedCount ? (show === 'notApplied' ? ' ▴' : ' ▾') : ''}
        </span>
        <span style={{ ...S.bannerStat, color: missingLockCount ? 'oklch(55% 0.15 40)' : undefined, cursor: missingLockCount ? 'pointer' : undefined }}
          onClick={() => missingLockCount && setShow(s => s === 'missingLock' ? null : 'missingLock')}>
          🔓 <b>{missingLockCount}</b> of {lockCheckedCount} EW matters missing a lock{missingLockCount ? (show === 'missingLock' ? ' ▴' : ' ▾') : ''}
        </span>
        {errorCount > 0 && <span style={S.bannerStat}>· {errorCount} couldn’t be checked</span>}
        {hasExceptions && (
          <button style={S.exportBtn} onClick={() => exportAuditCsv(result)}>Export to Excel (CSV)</button>
        )}
      </div>
      {groups.length > 0 && (
        <div style={S.groupWrap}>
          <div style={S.groupHint}>{groups.length} client{groups.length > 1 ? 's' : ''} · grouped by client number</div>
          {groups.map(([client, matters]) => (
            <div key={client} style={S.group}>
              <div style={S.groupHead}>
                {client === 'Other' ? 'Other' : `Client ${client}`}
                <span style={S.groupCount}>{matters.length}</span>
              </div>
              <div style={S.groupMatters}>
                {matters.map((mt, i) => (
                  <span key={i} style={S.matterChip}>{client === 'Other' ? mt.name : `.${mt.matter}`}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MembersPanel({ data }) {
  const {
    users = [], groups = [], defaults = [], workspaces = [],
    clientNumber, clientName, matterNumber, matterName, applied,
  } = data;
  // Prefer the workspace list (handles cross-matter barriers); fall back to
  // the single client/matter parsed from the policy name.
  const hasWs = workspaces.length > 0;
  return (
    <div>
      <div style={S.cmRow}>
        <span style={S.cmItem}>
          <span style={S.cmLabel}>Status</span>
          <span style={applied ? S.appliedOn : S.appliedOff}>
            {applied ? '✓ Applied to workspace' : '⚠ Not applied to any workspace'}
          </span>
        </span>
        {defaults.map((d, i) => (
          <span key={i} style={S.cmItem}>
            <span style={S.cmLabel}>Cabinet default</span>
            <span style={S.cmVal}>{d.access}</span>
          </span>
        ))}
        {!hasWs && clientName && (
          <span style={S.cmItem}>
            <span style={S.cmLabel}>Client</span>
            <span style={S.cmVal}>{clientNumber} · {clientName}</span>
          </span>
        )}
        {!hasWs && matterName && (
          <span style={S.cmItem}>
            <span style={S.cmLabel}>Matter</span>
            <span style={S.cmVal}>{matterNumber} · {matterName}</span>
          </span>
        )}
      </div>

      {hasWs && <WorkspaceList workspaces={workspaces}/>}

      <div className="wsm-members" style={S.cols}>
        <div style={S.col}>
          <div style={S.colHead}>Users ({users.length})</div>
          {users.length === 0 && <div style={S.mutedSm}>None</div>}
          {users.map(u => (
            <div key={u.id} style={S.member}>
              <span style={S.mIcon}><Icon name="user" size={13}/></span>
              <span className="wsm-mname" style={S.mName}>{u.displayName}</span>
              <span style={accessStyle(u.access)}>{u.access}</span>
            </div>
          ))}
        </div>
        <div style={S.col}>
          <div style={S.colHead}>Groups ({groups.length})</div>
          {groups.length === 0 && <div style={S.mutedSm}>None</div>}
          {groups.map(g => (
            <div key={g.id} style={S.member}>
              <span style={S.mIcon}><Icon name="users" size={13}/></span>
              <span className="wsm-mname" style={S.mName}>{g.displayName}</span>
              <span style={accessStyle(g.access)}>{g.access}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Access-level chip - "No Access" reads as a denial (amber), any grant is neutral.
const accessStyle = (access) => access === 'No Access' ? S.accNone : S.accGrant;

// Applied-workspaces list. Compact chips (client.matter, full name on
// hover); collapsed to the first N with a "show all" toggle so a barrier
// applied to dozens of matters doesn't blow out the panel.
function WorkspaceList({ workspaces }) {
  const [expanded, setExpanded] = useState(false);
  const CAP = 12;
  const shown = expanded ? workspaces : workspaces.slice(0, CAP);
  return (
    <div style={S.wsBlock}>
      <div style={S.colHead}>Applied to {workspaces.length} workspace{workspaces.length > 1 ? 's' : ''}</div>
      <div style={S.wsChips}>
        {shown.map((w, i) => {
          const parsed = w.client && w.matter;
          const label = parsed ? `${w.client}.${w.matter}` : (w.matterName || w.raw || 'unknown');
          return (
            <span key={i} title={w.matterName || undefined}
              style={parsed ? S.wsChip : S.wsChipRaw}>
              {parsed ? label : (label.length > 44 ? label.slice(0, 44) + '…' : label)}
            </span>
          );
        })}
        {workspaces.length > CAP && (
          <button style={S.wsMore} onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Show fewer' : `Show all ${workspaces.length}`}
          </button>
        )}
      </div>
    </div>
  );
}

const S = {
  wrap:      { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 },
  header:    { padding: '16px 24px 12px', borderBottom: '1px solid var(--line)' },
  headerRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  h1:        { fontSize: 18, fontWeight: 650, margin: 0, letterSpacing: '-0.01em' },
  count:     { fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-subtle)' },
  auditBtn:  { padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius)',
               border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', cursor: 'pointer' },
  searchRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '7px 12px',
               border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--bg-elev)', maxWidth: 520, color: 'var(--text-subtle)' },
  search:    { flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 14, color: 'var(--text)', fontFamily: 'inherit' },

  banner:    { padding: '10px 24px', borderBottom: '1px solid var(--line)', background: 'var(--bg-sub)' },
  bannerRow: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 13, color: 'var(--text)' },
  bannerStat:{ display: 'inline-flex', gap: 4 },
  bannerToggle: { marginLeft: 'auto', fontSize: 12, background: 'none', border: '1px solid var(--line)',
                  borderRadius: 'var(--radius)', padding: '3px 10px', cursor: 'pointer', color: 'var(--text-muted)' },
  exportBtn: { marginLeft: 'auto', fontSize: 12, fontWeight: 600, background: 'var(--bg-elev)',
               border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: '4px 12px',
               cursor: 'pointer', color: 'var(--accent-text)' },
  groupWrap: { marginTop: 12, maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 },
  groupHint: { fontSize: 11, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  group:     { display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap',
               paddingBottom: 6, borderBottom: '1px solid var(--line)' },
  groupHead: { fontSize: 12, fontWeight: 600, color: 'var(--text)', minWidth: 110, display: 'flex', gap: 6, alignItems: 'baseline' },
  groupCount:{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-subtle)' },
  groupMatters: { display: 'flex', flexWrap: 'wrap', gap: 5, flex: 1 },
  matterChip:{ fontFamily: 'var(--mono)', fontSize: 11, padding: '1px 7px', borderRadius: 4,
               background: 'var(--bg-elev)', color: 'var(--text-muted)', border: '1px solid var(--line)' },

  list:      { flex: 1, overflowY: 'auto', minHeight: 0, padding: '8px 0' },
  muted:     { padding: '20px 24px', color: 'var(--text-muted)', fontSize: 13 },
  mutedSm:   { padding: '8px 0', color: 'var(--text-subtle)', fontSize: 12 },
  err:       { padding: '16px 24px', color: 'oklch(55% 0.18 25)', fontSize: 13 },

  row:       { borderBottom: '1px solid var(--line)' },
  rowHead:   { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 24px', cursor: 'pointer' },
  pName:     { fontSize: 13, fontWeight: 500, color: 'var(--text)' },
  rowFlags:  { marginLeft: 'auto', display: 'inline-flex', gap: 10 },
  rowWarn:   { fontSize: 11, fontWeight: 600, color: 'oklch(55% 0.15 40)', whiteSpace: 'nowrap' },

  detail:    { padding: '4px 24px 18px 46px' },
  cmRow:     { display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--line)' },
  cmItem:    { display: 'flex', flexDirection: 'column', gap: 2 },
  cmLabel:   { fontSize: 10, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  cmVal:     { fontSize: 13, color: 'var(--text)', fontWeight: 500 },
  appliedOn:  { fontSize: 12, fontWeight: 600, color: 'oklch(52% 0.13 150)' },
  appliedOff: { fontSize: 12, fontWeight: 600, color: 'oklch(58% 0.14 70)' },

  wsBlock:   { marginBottom: 16 },
  wsChips:   { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  wsChip:    { fontSize: 12, padding: '3px 9px', borderRadius: 999, background: 'var(--accent-soft)',
               color: 'var(--accent-text)' },
  wsChipRaw: { fontSize: 11, padding: '3px 9px', borderRadius: 4, background: 'var(--bg-sub)',
               color: 'var(--text-subtle)', fontFamily: 'var(--mono)', maxWidth: 320,
               overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  wsMore:    { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
               border: '1px solid var(--line)', background: 'var(--bg-elev)', color: 'var(--text-muted)',
               cursor: 'pointer' },

  modalWrap: { position: 'fixed', inset: 0, background: 'oklch(0% 0 0 / 0.4)', display: 'flex',
               alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal:     { background: 'var(--bg-elev)', borderRadius: 10, boxShadow: 'var(--shadow-pop)',
               padding: '22px 24px', width: 380, maxWidth: '92vw' },
  modalTitle:{ fontSize: 15, fontWeight: 650, color: 'var(--text)' },
  modalSub:  { fontSize: 12, color: 'var(--text-muted)', marginTop: 4, marginBottom: 16 },
  progTrack: { height: 8, borderRadius: 999, background: 'var(--bg-sub)', overflow: 'hidden' },
  progFill:  { height: '100%', background: 'var(--accent)', transition: 'width 200ms ease' },
  progText:  { fontSize: 12, color: 'var(--text-muted)', marginTop: 8, fontFamily: 'var(--mono)' },
  modalStats:{ display: 'flex', gap: 16, marginTop: 12, fontSize: 13, color: 'var(--text)' },
  modalFoot: { marginTop: 18, display: 'flex', justifyContent: 'flex-end' },
  modalCancel:{ fontSize: 12, padding: '6px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--line)',
                background: 'var(--bg)', color: 'var(--text-muted)', cursor: 'pointer' },

  cols:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  col:       { minWidth: 0 },
  colHead:   { fontSize: 11, fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase',
               letterSpacing: '0.04em', marginBottom: 8 },
  member:    { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, minWidth: 0 },
  mIcon:     { color: 'var(--text-subtle)', display: 'inline-flex', flexShrink: 0 },
  mName:     { color: 'var(--text)', whiteSpace: 'nowrap' },
  accGrant:  { marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600,
               color: 'var(--accent-text)', background: 'var(--accent-soft)', padding: '1px 6px', borderRadius: 4 },
  accNone:   { marginLeft: 'auto', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600,
               color: 'oklch(50% 0.02 260)', background: 'var(--bg-sub)', padding: '1px 6px', borderRadius: 4 },
};
